import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, TableContainer, Table, Paper, TableHead, TableRow, TableCell, TableBody, TextField, Grid, TablePagination, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Select, MenuItem, TableSortLabel, IconButton, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../context/AuthContext';
import DataQualityDashboard from './DataQualityDashboard';

function fetchIngredients() {
    return fetch(`${import.meta.env.VITE_API_URL}/ingredients`).then(response => response.json());
}

function fetchUsageCounts() {
    return fetch(`${import.meta.env.VITE_API_URL}/ingredients/usage-count/all`).then(response => response.json());
}

function GestionIngredientes() {
    const { userRole, authToken } = useAuth();
    console.log("El rol actual es " + userRole);
    const isAdmin = userRole === 'admin';
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [filterReviewed, setFilterReviewed] = useState('all');
    const [ingredients, setIngredients] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderDirection, setOrderDirection] = useState('asc'); // Nuevo estado para la dirección del ordenamiento
    const [orderBy, setOrderBy] = useState(''); // Nuevo estado para la columna por la que se ordena
    const [usageCounts, setUsageCounts] = useState({}); // Nuevo estado para el conteo de usos

    React.useEffect(() => {
        if (!isAdmin) {
            navigate('/dashboard');
        }
    }, [isAdmin, navigate]);

    useEffect(() => {
        Promise.all([
            fetchIngredients(),
            fetchUsageCounts()
        ]).then(([ingredientsData, usageCountsData]) => {
            setIngredients(ingredientsData);
            setUsageCounts(usageCountsData);
        });
    }, []);

    // Filtrar ingredientes basado en la búsqueda
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && orderDirection === 'asc';
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortIngredients = (array) => {
        return array.sort((a, b) => {
            if (orderBy === '') return 0;

            let aValue, bValue;

            // Manejar ordenamiento especial para usageCount
            if (orderBy === 'usageCount') {
                aValue = usageCounts[a._id] || 0;
                bValue = usageCounts[b._id] || 0;
            } else {
                aValue = a[orderBy];
                bValue = b[orderBy];
            }

            if (aValue < bValue) {
                return orderDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return orderDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const normalize = (s) => (s || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    const filteredIngredients = sortIngredients(ingredients
        .filter(ingredient => normalize(ingredient.name).includes(normalize(search)))
        .filter(ingredient => filterReviewed === 'all' || (filterReviewed === 'yes' ? ingredient.revisado : !ingredient.revisado)))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleClickOpen = (ingredient) => {
        console.log('Opening dialog for ingredient:', ingredient); // Agrega este log para depuración
        setSelectedIngredient(ingredient);
        setOpen(true);
    };


    const handleClose = () => {
        setOpen(false);
    };

    const handleDuplicate = async (ingredient) => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/ingredients/${ingredient._id}/duplicate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const newIngredient = await response.json();
                navigate(`/editar-ingrediente/${newIngredient._id}`);
            } else {
                console.error("Error duplicando ingrediente");
            }
        } catch (error) {
            console.error("Error duplicando ingrediente:", error);
        }
    };

    const handleDelete = () => {
        fetch(`${import.meta.env.VITE_API_URL}/ingredients/${selectedIngredient._id}`, {
            method: 'DELETE',
        })
            .then(response => response.json())
            .then(data => {
                if (data.message === "Ingrediente eliminado") {
                    // Actualizar la lista de ingredientes después de eliminar
                    setIngredients(ingredients.filter(ingredient => ingredient._id !== selectedIngredient._id));
                    // Actualizar también el conteo de usos
                    const newUsageCounts = { ...usageCounts };
                    delete newUsageCounts[selectedIngredient._id];
                    setUsageCounts(newUsageCounts);
                } else {
                    console.error('Error:', data);
                }
            })
            .catch((error) => {
                console.error('Error:', error);
            });

        setOpen(false);
    };

    return (
        <Grid className="modern-admin-page" container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    label="Buscar ingrediente"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0); // Lleva al usuario a la primera página
                    }}
                />
                <Select
                    value={filterReviewed}
                    onChange={(e) => setFilterReviewed(e.target.value)}
                >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="yes">Revisado</MenuItem>
                    <MenuItem value="no">No revisado</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={12}>
                <Button variant="contained" sx={{ bgcolor: 'primary.main', color: '#fff' }} component={Link} to="/crear-ingrediente">
                    Crear Ingrediente
                </Button>
            </Grid>
            <Grid item xs={12}>
                <DataQualityDashboard data={ingredients} type="ingredients" />
            </Grid>
            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'kilocalories'}
                                        direction={orderBy === 'kilocalories' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('kilocalories')}
                                    >
                                        Calorías
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'carbs'}
                                        direction={orderBy === 'carbs' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('carbs')}
                                    >
                                        Carbohidratos
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'protein'}
                                        direction={orderBy === 'protein' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('protein')}
                                    >
                                        Proteínas
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'fat'}
                                        direction={orderBy === 'fat' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('fat')}
                                    >
                                        Grasas
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'gramsPerUnit'}
                                        direction={orderBy === 'gramsPerUnit' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('gramsPerUnit')}
                                    >
                                        Gramos por Unidad
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'usageCount'}
                                        direction={orderBy === 'usageCount' ? orderDirection : 'asc'}
                                        onClick={() => handleRequestSort('usageCount')}
                                    >
                                        Usos
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Revisado</TableCell>
                                <TableCell>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredIngredients.map((ingredient) => (
                                <TableRow key={ingredient._id}>
                                    <TableCell>{ingredient.name}</TableCell>
                                    <TableCell>{parseFloat(ingredient.kilocalories).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell>{parseFloat(ingredient.carbs).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell>{parseFloat(ingredient.protein).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell>{parseFloat(ingredient.fat).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                    <TableCell>{ingredient.gramsPerUnit}</TableCell> {/* Asumiendo que gramsPerUnit es un valor entero o no necesita formato decimal */}
                                    <TableCell>
                                        <span className={`ingredient-usage-count ${usageCounts[ingredient._id] === 0 || !usageCounts[ingredient._id] ? 'zero' :
                                                usageCounts[ingredient._id] >= 5 ? 'high' : ''
                                            }`}>
                                            {usageCounts[ingredient._id] || 0}
                                        </span>
                                    </TableCell>
                                    <TableCell>{ingredient.revisado ? 'Si' : 'No'}</TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            <Button variant="contained" sx={{ bgcolor: 'primary.main', color: '#fff' }} component={Link} to={`/editar-ingrediente/${ingredient._id}`}>
                                                Editar
                                            </Button>
                                            <IconButton
                                                onClick={() => handleDuplicate(ingredient)}
                                                size="small"
                                                aria-label="Duplicar ingrediente"
                                                sx={{ color: 'info.main' }}
                                            >
                                                <ContentCopyIcon />
                                            </IconButton>
                                            <Button variant="contained" color="secondary" onClick={() => handleClickOpen(ingredient)}>
                                                Eliminar
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={ingredients.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            </Grid>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Eliminar Ingrediente"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        ¿Estás seguro de que deseas eliminar este ingrediente?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color="secondary" autoFocus>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

        </Grid>

    );
}

export default GestionIngredientes;
